import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Activity, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const RANGES = [7, 30, 90];

const label = (key) =>
  key === "direct" ? "Direct / none" : key === "internal" ? "On site" : key;

/** Headline first-party traffic, straight from our own collector. */
export function TrafficCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/admin/traffic/summary", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const delta =
    data && data.prev_views > 0
      ? Math.round(((data.views - data.prev_views) / data.prev_views) * 100)
      : null;
  const Trend = delta === null || delta === 0 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
  const trendColor = delta > 0 ? "text-champagne" : delta < 0 ? "text-signal-red" : "text-aubergine/45";

  return (
    <section
      className="mb-8 rounded-lg border border-aubergine/10 bg-white overflow-hidden"
      data-testid="traffic-card"
    >
      <div className="flex items-center justify-between gap-4 border-b border-aubergine/10 px-6 py-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Traffic
          </p>
          <p className="mt-1 text-xs text-aubergine/55">
            First-party, cookieless. No data leaves your server.
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`traffic-range-${r}`}
              aria-pressed={days === r}
              className={`rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                days === r
                  ? "border-champagne bg-champagne/10 text-aubergine"
                  : "border-aubergine/15 text-aubergine/55 hover:border-aubergine/35"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="px-6 py-10 font-mono text-xs uppercase tracking-wider text-aubergine/45">
          Loading…
        </p>
      ) : !data ? (
        <p className="px-6 py-10 text-sm text-aubergine/55">Traffic data unavailable.</p>
      ) : data.views === 0 ? (
        <div className="px-6 py-10" data-testid="traffic-empty">
          <p className="font-serif text-xl text-aubergine">No visits recorded yet</p>
          <p className="mt-2 max-w-md text-sm text-aubergine/60">
            Counting starts as soon as this is live. Numbers appear here within seconds of the
            first visit, there is nothing to configure.
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-6 px-6 py-5 sm:grid-cols-[auto_auto_1fr] sm:items-end">
            <div>
              <p className="font-serif text-4xl text-aubergine" data-testid="traffic-views">
                {data.views.toLocaleString()}
              </p>
              <p className="mt-1 text-sm font-medium text-aubergine">Pageviews</p>
              {delta !== null && (
                <p className={`mt-0.5 flex items-center gap-1 font-mono text-[11px] ${trendColor}`}>
                  <Trend className="h-3 w-3" />
                  {Math.abs(delta)}% vs previous {data.days}d
                </p>
              )}
            </div>
            <div>
              <p className="font-serif text-4xl text-aubergine" data-testid="traffic-visitors">
                {data.visitors.toLocaleString()}
              </p>
              <p className="mt-1 text-sm font-medium text-aubergine">Visitors</p>
              <p className="mt-0.5 font-mono text-[11px] text-aubergine/45">
                {data.devices.map((d) => `${d.key} ${Math.round((d.views / data.views) * 100)}%`).join(" · ")}
              </p>
            </div>
            <Sparkline series={data.series} />
          </div>

          <div className="grid gap-px bg-aubergine/10 sm:grid-cols-2">
            <TopList title="Top pages" rows={data.top_pages} testid="traffic-top-pages" />
            <TopList title="Top sources" rows={data.top_referrers} testid="traffic-top-referrers" render={label} />
          </div>
        </>
      )}
    </section>
  );
}

function Sparkline({ series }) {
  const max = Math.max(...series.map((d) => d.views), 1);
  return (
    <div className="flex h-16 items-end gap-[2px]" data-testid="traffic-sparkline">
      {series.map((d) => (
        <div
          key={d.day}
          title={`${d.day}: ${d.views} views, ${d.visitors} visitors`}
          className="flex-1 rounded-sm bg-champagne/70 hover:bg-champagne transition-colors"
          style={{ height: `${Math.max((d.views / max) * 100, 2)}%`, minWidth: "2px" }}
        />
      ))}
    </div>
  );
}

function TopList({ title, rows, testid, render = (k) => k }) {
  const max = Math.max(...rows.map((r) => r.views), 1);
  return (
    <div className="bg-white px-6 py-5" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-aubergine/55">{title}</p>
      <div className="mt-3 space-y-1.5">
        {rows.length === 0 && <p className="text-sm text-aubergine/45">Nothing yet.</p>}
        {rows.map((r) => (
          <div key={r.key} className="relative">
            <div
              className="absolute inset-y-0 left-0 rounded-sm bg-champagne/15"
              style={{ width: `${(r.views / max) * 100}%` }}
            />
            <div className="relative flex items-baseline justify-between gap-3 px-2 py-1">
              <span className="truncate text-sm text-aubergine">{render(r.key)}</span>
              <span className="flex-none font-mono text-[11px] tabular-nums text-aubergine/55">
                {r.views.toLocaleString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
